import { Injectable } from '@angular/core';
import {
    AbstractControl,
    AbstractControlOptions,
    AsyncValidatorFn,
    FormArray,
    FormBuilder,
    FormControl,
    FormControlState,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    ɵFormGroupRawValue,
} from '@angular/forms';
import { merge } from 'rxjs';
import { dateIstAfterToday, getDateToday } from './date-utils';

export function markControlsWithErrorsAsDirty(...abstractControls: AbstractControl[]): void {
    abstractControls.forEach((abstractControl) => {
        if (abstractControl.errors) {
            abstractControl.markAsDirty({ onlySelf: true });
        }

        if (abstractControl instanceof FormGroup) {
            markControlsWithErrorsAsDirty(...Object.values((abstractControl as FormGroup).controls));
        } else if (abstractControl instanceof FormArray) {
            markControlsWithErrorsAsDirty(...(abstractControl as FormArray).controls);
        }
    });
}

export function setError(control: AbstractControl | null, errorKey: string, value: unknown = true): ValidationErrors {
    control?.setErrors({ [errorKey]: value, ...control?.errors });
    return { [errorKey]: value };
}

export function unsetError(control: AbstractControl | null, errorKey: string): null {
    if (!control) return null;

    const { [errorKey]: omitError, ...errors } = control.errors ?? {};

    if (Object.keys(errors).length === 0) {
        control.setErrors(null);
    } else {
        control.setErrors(errors);
    }
    return null;
}

/**
 * Erstellt einen Typ mit allen Properties von T rekursiv als nullable deklariert.
 */
type Nullable<T> = T extends Array<unknown> ? T | null : T extends Date ? T | null : T extends object ? { [P in keyof T]: Nullable<T[P]> | null } : T | null;

/**
 * Gibt FormGroup.value zurück. Anders als FormGroup.value werden Properties zu Controls,
 * die disabled sind, jedoch nicht komplett ausgelassen, sondern erhalten den Wert null.
 */
export function nulledValues<T extends { [K in keyof T]: AbstractControl<unknown> }>(form: FormGroup<T>): Nullable<ɵFormGroupRawValue<T>> {
    return transformDisabledFieldsToNull(form.getRawValue(), form.value) as Nullable<ɵFormGroupRawValue<T>>;
}

function transformDisabledFieldsToNull(allValues: unknown, enabledValues: unknown): unknown {
    if (Array.isArray(allValues) && Array.isArray(enabledValues)) {
        return allValues.map((v, i) => transformDisabledFieldsToNull(v, enabledValues[i]));
    }
    if (allValues instanceof Date) {
        return new Date(allValues);
    }
    if (allValues !== null && typeof allValues === 'object' && enabledValues !== null && typeof enabledValues === 'object') {
        return Object.fromEntries(
            Object.entries(allValues).map(([prop, value]) => [
                prop,
                prop in enabledValues ? transformDisabledFieldsToNull(value, (enabledValues as { [key: string]: unknown })[prop]) : null,
            ]),
        );
    }
    return enabledValues ?? null;
}

/**
 * Typische Properties von Model-Typen, die üblicherweise nicht über FormGroups editiert werden.
 */
export type NonEditableFields = 'id' | 'version';

/**
 * Abgeleiteter Typ eines Model-Typs, exklusive bestimmter Properties (i.e. NonEditableFields und als String
 * Union als zweiter Generic-Parameter übergebene Properties), die nicht als Teil einer FormGroup editiert werden.
 */
export type FormVO<T, O extends keyof T = never> = Omit<T, O | NonEditableFields>;

/**
 * Alias für eine FormGroups, dessen Typisierung den Properties des generischen Typen T entspricht.
 * Properties die dem generischen Parameter F entspreichen, werden rekursiv als FormModel behandelt.
 */
export type FormModel<T, F extends string = never> = FormGroup<FormGroupType<NonNullable<T>, F>>;

export type FormGroupProperty<K, P, V, F extends string = never> =
    NonNullable<P> extends Date ? V : K extends F ? (NonNullable<P> extends Array<infer U> ? FormArray<AbstractControl<U>> : FormModel<P, F>) : V;

export type FormGroupType<T, F extends string = never> = {
    [P in keyof FormVO<T>]: FormGroupProperty<P, T[P], FormControl<T[P]>, F>;
};

/**
 * Entspricht ControlConfig<T> aus dem Angular Forms Modul, mit dem Unterschied, dass an Stelle der ValidatorFn nicht nur undefined, sondern auch null stehen kann.
 * Ist notwendig, damit an der Stelle ein Validator.compose() übergeben werden kann.
 *
 * Validator.compose() returnt nämlich komischerweise ValidatorFn | null und nicht nur ValidatorFn oder ValidatorFn | undefined.
 * Das funktioniert eigentlich auch mit FormBuilder.group() nicht 100% richtig (Typ des Controls wird nullable, auch wenn der nonNullable FormBuilder verwendet wird),
 * da hat das Angular Team wohl was verabsäumt, behaupte ich mal.
 */
type NullableValidatorControlConfig<T> = [T | FormControlState<T>, (ValidatorFn | ValidatorFn[] | null)?, (AsyncValidatorFn | AsyncValidatorFn[] | null)?];

/**
 * Leitet für einen Model-Typ T die möglichen Control-Konfigurationsoptionen zur Erstellung von FormGroups
 * mittels ModelFormBuilder.model() ab.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export type FormModelConfig<T extends {}, F extends string = never> = {
    [K in keyof Omit<T, NonEditableFields>]: FormGroupProperty<K, T[K], FormModelPropConfig<T[K]>, F>;
};

export type FormModelPropConfig<T> = T | FormControl<T> | FormControlState<T> | NullableValidatorControlConfig<T>;

export type FormModelOptions<T> = (AbstractControlOptions & { initialValues?: Partial<T> | null }) | null;

/**
 * ModelFormBuilder bietet mit der Methode model analog zum Angular FormBuilder.group eine Möglichkeit an,
 * FormGroups (als FormModel<T>) zu erstellen. Der Typisierung des FormGroups wird dabei durch Angabe eines Model-Typs als
 * Generic festgelegt; die Konfiguration der Controls wird daher auch vom Model-Typ abgeleitet und vorbestimmt.
 *
 * Dies steht im Gegensatz FormBuilder.group, bei dem die Typisierung des resultierenden FormGroups von der übergebenen
 * Controls-Konfiguration abgeleitet wird und man ggf. selber darauf achten muss, dass Properties und Datentypen
 * denen des Model-Typs entsprechen.
 *
 * Beispiel:
 *
 * class ModelVO {
 *     id: number | null = null;
 *     version: number | null = null;
 *     label: string | null = null;
 *     data: string | null = null;
 *     flag: boolean | null = null;
 * }
 *
 * const initData: ModelVO = new ModelVO();
 *
 * const form: FormModel<ModelVO> = fb.model<ModelVO>({
 *     // da der Typ von diesem Control bereits durch die Angabe von ModelVO als generischen Typen bekannt ist,
 *     // kann es an dieser Stelle ohne weitere Typenangabe auf einen string oder null gesetzt werden
 *     label: null,
 *     // abgesehen von der vorbestimmten Typisierung können Controls wie von FormBuilder.group gewohnt konfiguriert werden.
 *     data: [null, Validators.required],
 *     // es müssen alle Properties, die im Model-Typ vorkommen auch entsprechend als Control konfiguriert werden. Es können
 *     // keine zusätzlichen Controls konfiguriert werden, die im Model-Typ nicht vorkommen. Ist das gewüscht, muss der Typ vorher
 *     // modifiziert werden und der modifizierte Typ als Generic angegeben werden,
 *     //z.B. mit dem Untility Type FormVO: type ModelFormVO = FormVO<ModelVO, 'flag'> wenn flag kein Control sein soll
 *     flag: true,
 * }, {
 *      // model Methode bietet zusätzlich die Option initalValues an, die die obig Konfigurierten Defaultwerte überschreibt, wenn die Properties nicht null oder undefined sind
 *     initialValues: initData
 * });
 *
 * const values: FormVO<ModelVO> = form.value;
 *
 * Um verschachtelte FormGroups bzw. FormModels zu generieren, müssen die entsprechenden Properties als generischer Parameter F angegeben werden.
 * Siehe auch Doc zu {@link FormModel}.
 *
 */
@Injectable({
    providedIn: 'root',
})
export class ModelFormBuilder extends FormBuilder {
    // T = Model type
    // eslint-disable-next-line @typescript-eslint/ban-types
    public model<T extends {}, F extends string = never, U extends T = T>(controls: FormModelConfig<T, F>, options?: FormModelOptions<U>): FormModel<T, F> {
        // cast zu as FormGroup (i.e. FormGroup<any>) um eine manuelle Transformation vom FormBuilder.group Return-Type
        // FormGroup<{ [K in keyof C]: ɵElement<C[K], null>; }> mit C = FormModelConfig<T> zu FormModel<T> zu umgehen
        const formModel = this.group(controls, options) as FormGroup;
        patchValue(formModel, options?.initialValues);
        return formModel;
    }
}

/**
 * Wie AbstractControl.patchValue, null und undefined Werte werden aber nicht übernommen, wodurch Defaultwerte bestehen bleiben
 */
export function patchValue(control: AbstractControl | null, value: any): void {
    if (!control || value == null) {
        return;
    }
    if (control instanceof FormGroup) {
        for (const key in value) {
            patchValue(control.get(key), value[key]);
        }
    }
    if (control instanceof FormArray) {
        let i = 0;
        for (const v of value) {
            patchValue(control.at(i++), v);
        }
    }
    if (control instanceof FormControl) {
        control.setValue(value, { emitEvent: false, onlySelf: true });
    }
}

export function isDateBefore(date?: Date): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const calenderDate = new Date(control.value);

        if (date && calenderDate < date) {
            return { datevalidation: { value: control.value } };
        }
        return null;
    };
}

export function onValueChange(watchValues: string[], item: any, handler: (value: unknown) => void): void {
    merge(
        ...watchValues
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((v) => [v + 'ValueChanges'] ?? item.get(v)?.valueChanges)
            .filter((o) => o),
    ).subscribe(handler);
}

export function setzeGueltigVon(form: FormGroup, gueltigVon?: Date | null): void {
    if (gueltigVon && dateIstAfterToday(gueltigVon)) {
        form.patchValue({ gueltigVon: gueltigVon });
    } else {
        form.patchValue({ gueltigVon: getDateToday() });
    }
}

export function getInvalidControlNames(input: FormGroup | FormArray): string[] {
    const invalidControlNames: string[] = [];
    Object.keys(input.controls).forEach((controlName) => {
        const control = input.get(controlName)!;
        if (control.invalid && control instanceof FormControl) {
            for (const errorsKey in control.errors) {
                if (errorsKey === 'invalidForm') {
                    if (control.errors[errorsKey].message) {
                        invalidControlNames.push(`${control.errors[errorsKey].message}`);
                    }
                } else {
                    invalidControlNames.push(`${formatFormControlName(controlName)}`);
                }
            }
        } else if (control.invalid && (control instanceof FormGroup || control instanceof FormArray)) {
            invalidControlNames.push(...getInvalidControlNames(control));
        }
    });
    return [...new Set(invalidControlNames)];
}

function capitalizeFirstLetter(word: string): string {
    return word.charAt(0).toUpperCase() + word.slice(1);
}

function umlauts(word: string): string {
    // prettier-ignore
    return word
        .replace(/ae/g, 'ä')
        .replace(/oe/g, 'ö')
        .replace(/ue/g, 'ü')
        .replace(/Ae/g, 'Ä')
        .replace(/Oe/g, 'Ö')
        .replace(/Ue/g, 'Ü');
}

function formatFormControlName(input: string): string {
    const words = input.split(/(?=[A-Z])/);

    const formattedWords = words.map(capitalizeFirstLetter).map(umlauts);

    return formattedWords.join(' ');
}
